/**
 * Voxel lighting — two independent channels baked into the packed 32-bit word:
 *
 * 1. SKY light (low 5 bits): top-down sunlight column + horizontal BFS spread.
 *    Air gets full light, opaque solids stop the column, transparent/liquid pass
 *    light through. Modulates the PBR sun in the terrain shader (time-of-day).
 * 2. BLOCK light (bits 16-20): emitters only (e.g. lava), flooded by the same
 *    −1/step max-merge BFS across all 6 faces. Sun-independent — glows at night.
 *
 * Both run client-side before meshing, in-place on the resident chunk word —
 * server sends 0 in both fields. The two fields never interfere (each pass
 * clears + rewrites only its own bits).
 */
import { CHUNK_SIZE, LIGHT_MASK, LIGHT_MAX, LIGHT_BITS, MATERIAL_BITS, MATERIAL_MASK, SURFACE_PACKED_THRESHOLD, VOXELS_PER_CHUNK, BLOCK_LIGHT_SHIFT, BLOCK_LIGHT_MASK, BLOCK_LIGHT_CLEAR, VOXEL_STATIC_MASK, } from './constants.js';
import { MATERIAL_TYPE_LUT, MAT_TYPE_SOLID, MATERIAL_EMISSION_LUT } from '../materials/Materials.js';
/** Stride constants for flat index = x + y*CHUNK_SIZE + z*CHUNK_SIZE² */
const Y_STRIDE = CHUNK_SIZE;
const Z_STRIDE = CHUNK_SIZE * CHUNK_SIZE;
/** Inverse light mask for clearing sky-light bits: ~0b11111 */
const LIGHT_CLEAR = ~LIGHT_MASK;
/** Positioned block-light field mask (bits 16-20), for fast "has block light?" tests. */
const BLOCK_LIGHT_FIELD = BLOCK_LIGHT_MASK << BLOCK_LIGHT_SHIFT;
const MAX = CHUNK_SIZE - 1;
// ============== Pre-computed LUTs (single-lookup light blocking + emission) ==============
/**
 * Light-blocking LUT indexed by `(voxel & VOXEL_STATIC_MASK) >> LIGHT_BITS`
 * (the 11 weight+material bits, with block-light/spare bits masked off).
 * Entry is 1 if the voxel is opaque solid (blocks light propagation), 0 otherwise.
 * Replaces isVoxelSolid() + MATERIAL_TYPE_LUT lookup in hot paths.
 * 2048 entries, fits comfortably in L1 cache.
 */
const VOXEL_BLOCKS_LIGHT = new Uint8Array(1 << (16 - LIGHT_BITS));
/**
 * Emission LUT indexed by `(voxel & VOXEL_STATIC_MASK) >> LIGHT_BITS`
 * (the 11 weight+material bits). Returns emission level (0-31) for solid emissive
 * voxels, 0 for everything else. Combines the isVoxelSolid() check +
 * MATERIAL_EMISSION_LUT lookup into one read. Seeds the block-light channel.
 */
const VOXEL_EMISSION = new Uint8Array(1 << (16 - LIGHT_BITS));
(function initLightingLUTs() {
    for (let wm = 0; wm < VOXEL_BLOCKS_LIGHT.length; wm++) {
        const weight = wm >> MATERIAL_BITS;
        const material = wm & MATERIAL_MASK;
        const isSolid = weight > SURFACE_PACKED_THRESHOLD;
        if (isSolid && MATERIAL_TYPE_LUT[material] === MAT_TYPE_SOLID) {
            VOXEL_BLOCKS_LIGHT[wm] = 1;
        }
        if (isSolid) {
            VOXEL_EMISSION[wm] = MATERIAL_EMISSION_LUT[material];
        }
    }
})();
/**
 * Face descriptors for border light injection.
 * Each entry: [ourFixedOffset, nbrFixedOffset, stride1, stride2]
 * Order: +X, -X, +Y, -Y, +Z, -Z (matches NEIGHBOR_DELTAS)
 */
const FACE_DESC = [
    [MAX, 0, Y_STRIDE, Z_STRIDE], // +X: our x=31, nbr x=0
    [0, MAX, Y_STRIDE, Z_STRIDE], // -X: our x=0,  nbr x=31
    [MAX * Y_STRIDE, 0, 1, Z_STRIDE], // +Y: our y=31, nbr y=0
    [0, MAX * Y_STRIDE, 1, Z_STRIDE], // -Y: our y=0,  nbr y=31
    [MAX * Z_STRIDE, 0, 1, Y_STRIDE], // +Z: our z=31, nbr z=0
    [0, MAX * Z_STRIDE, 1, Y_STRIDE], // -Z: our z=0,  nbr z=31
];
/**
 * Inject light from neighbor chunk boundaries into this chunk's edge voxels.
 *
 * For each loaded neighbor face, reads the neighbor's boundary voxel light,
 * decrements by 1, and writes it into our facing edge voxel if it exceeds
 * the current value. This seeds the subsequent BFS to spread light inward.
 *
 * @param data       This chunk's Uint32Array, modified in-place
 * @param neighbors  Array of 6 neighbor data arrays (+X, -X, +Y, -Y, +Z, -Z), null if not loaded
 */
export function injectBorderLight(data, neighbors, seedTail = 0) {
    let tail = seedTail;
    for (let face = 0; face < 6; face++) {
        const nData = neighbors[face];
        if (!nData)
            continue;
        const [ourBase, nbrBase, s1, s2] = FACE_DESC[face];
        for (let a = 0; a < CHUNK_SIZE; a++) {
            const aOff = a * s1;
            for (let b = 0; b < CHUNK_SIZE; b++) {
                const nbrIdx = nbrBase + aOff + b * s2;
                const nbrLight = nData[nbrIdx] & LIGHT_MASK;
                if (nbrLight <= 1)
                    continue;
                const ourIdx = ourBase + aOff + b * s2;
                const ourVoxel = data[ourIdx];
                // Skip opaque solid voxels (single LUT lookup; mask off block/spare bits)
                if (VOXEL_BLOCKS_LIGHT[(ourVoxel & VOXEL_STATIC_MASK) >> LIGHT_BITS])
                    continue;
                const newLight = nbrLight - 1;
                if (newLight > (ourVoxel & LIGHT_MASK)) {
                    data[ourIdx] = (ourVoxel & LIGHT_CLEAR) | newLight;
                    // Collect as BFS seed — border voxels always have a darker inward neighbor
                    if (newLight > 1)
                        bfsQueue[tail++] = ourIdx;
                }
            }
        }
    }
    return tail;
}
/**
 * True if this chunk's boundary layer on `face` could raise light in the neighbour on that side —
 * i.e. any boundary voxel carries sky OR block light > 1. Border injection (injectBorderLight /
 * injectBorderBlockLight) skips a source voxel whose light is <= 1, so a face that donates nothing
 * cannot change the neighbour's lit state at all.
 *
 * Used to skip the neighbour relight after a chunk arrives when that chunk contributes no border
 * light across the shared face — the common underground case (rock↔rock faces are fully dark). The
 * skip is output-preserving because a present-but-non-donating face is indistinguishable from an
 * absent one to the neighbour's injection pass. Face order matches FACE_DESC / FACE_OFFSETS_6:
 * 0:+X 1:-X 2:+Y 3:-Y 4:+Z 5:-Z.
 *
 * NOTE: this only covers the horizontal-BFS / block donation direction. It must NOT be used to skip
 * relighting the chunk BELOW an arrival: a solid arrival changes the below chunk's `lightFromAbove`
 * (open-sky → capped) even while donating no light, so the below relight is always required.
 */
export function faceDonatesLight(data, face) {
    const [ourBase, , s1, s2] = FACE_DESC[face];
    for (let a = 0; a < CHUNK_SIZE; a++) {
        const aOff = a * s1;
        for (let b = 0; b < CHUNK_SIZE; b++) {
            const v = data[ourBase + aOff + b * s2];
            if ((v & LIGHT_MASK) > 1)
                return true;
            if (((v >>> BLOCK_LIGHT_SHIFT) & BLOCK_LIGHT_MASK) > 1)
                return true;
        }
    }
    return false;
}
/**
 * Propagate sunlight columns through a chunk's voxel data (in-place).
 *
 * For each (lx, lz) column, determines the incoming light level from above,
 * then scans downward: air and transparent/liquid pass light through,
 * opaque solids stop the column.
 *
 * @param data          The chunk's Uint32Array (CHUNK_SIZE³ entries), modified in-place
 * @param lightFromAbove Per-column light level entering the top of this chunk (0-31).
 *                      Flat array of CHUNK_SIZE² values indexed as [lx + lz * CHUNK_SIZE].
 *                      If null, every column starts at LIGHT_MAX.
 * @returns             Per-column light level exiting the bottom of this chunk (0-31).
 *                      Same layout as lightFromAbove. Caller uses this to propagate to chunk below.
 */
export function computeSunlightColumns(data, lightFromAbove, contentOut) {
    const lightBelow = new Uint8Array(CHUNK_SIZE * CHUNK_SIZE);
    let sawEmitter = false;
    let sawBlockField = false;
    for (let lz = 0; lz < CHUNK_SIZE; lz++) {
        const zOff = lz * Z_STRIDE;
        for (let lx = 0; lx < CHUNK_SIZE; lx++) {
            const colIdx = lx + lz * CHUNK_SIZE;
            const colBase = lx + zOff;
            let light = lightFromAbove ? lightFromAbove[colIdx] : LIGHT_MAX;
            let lowestLitY = CHUNK_SIZE; // sentinel: no lit voxels in this column
            // Scan top (ly=31) down to bottom (ly=0)
            for (let ly = CHUNK_SIZE - 1; ly >= 0; ly--) {
                const idx = colBase + ly * Y_STRIDE;
                const voxel = data[idx];
                // Free ride for the BLOCK channel: this loop already loads every voxel and already computes
                // its material index, so noting "is there an emitter / any block light in this chunk" costs
                // two ops on data in registers. computeBlockLight would otherwise walk all 32768 voxels again
                // purely to answer the same question — measured as the dominant remaining cost of its skip.
                // Sky ops only ever rewrite the low LIGHT_MASK bits (LIGHT_CLEAR preserves material and the
                // block field), so these facts are still true when the block pass runs.
                const wm = (voxel & VOXEL_STATIC_MASK) >> LIGHT_BITS;
                if (contentOut !== undefined) {
                    if (VOXEL_EMISSION[wm] > 0)
                        sawEmitter = true;
                    if ((voxel & BLOCK_LIGHT_FIELD) !== 0)
                        sawBlockField = true;
                }
                if (VOXEL_BLOCKS_LIGHT[wm]) {
                    // Opaque solid: stop column
                    data[idx] = voxel & LIGHT_CLEAR;
                    light = 0;
                }
                else {
                    // Air / transparent / liquid: pass light through
                    data[idx] = (voxel & LIGHT_CLEAR) | light;
                    if (light > 0)
                        lowestLitY = ly;
                }
            }
            lightBelow[colIdx] = light;
            litBottom[colIdx] = lowestLitY;
        }
    }
    if (contentOut !== undefined) {
        contentOut.anyEmitter = sawEmitter;
        contentOut.anyBlockField = sawBlockField;
    }
    return lightBelow;
}
/**
 * Would injecting `source`'s boundary layer across `face` BRIGHTEN `target` anywhere?
 *
 * The pairwise companion to faceDonatesLight, and the exact test the relight skip actually needs.
 * faceDonatesLight only asks whether the SOURCE carries light — which is true for every face of a
 * sky-lit air chunk, so an arrival next to already-lit terrain relit all five neighbours even though
 * injection changed nothing. Measured: the below-face gate saved nothing because the cost was here —
 * 2.2 sunlight passes per arrival, ~69% of ingest time, most of them provable no-ops.
 *
 * Exact because border injection is MONOTONE INCREASING and position-local: injectBorderLight /
 * injectBorderBlockLight change a target voxel iff `srcLight > 1 && srcLight - 1 > tgtLight` and the
 * target isn't opaque (mirrored below, channel for channel — keep the three in step). Neither the
 * target's own data nor its lightFromAbove involves the source (a horizontal or +Y neighbour reads
 * the sky from its OWN column), so if no boundary voxel would change, re-lighting is a no-op.
 *
 * `face` is the source's face TOWARD the target (the same index faceDonatesLight takes).
 */
export function faceWouldBrighten(source, face, target) {
    const [srcBase, tgtBase, s1, s2] = FACE_DESC[face];
    for (let a = 0; a < CHUNK_SIZE; a++) {
        const aOff = a * s1;
        for (let b = 0; b < CHUNK_SIZE; b++) {
            const off = aOff + b * s2;
            const tgtVoxel = target[tgtBase + off];
            // Opaque target voxels take no light in either channel.
            if (VOXEL_BLOCKS_LIGHT[(tgtVoxel & VOXEL_STATIC_MASK) >> LIGHT_BITS])
                continue;
            const srcVoxel = source[srcBase + off];
            const srcSky = srcVoxel & LIGHT_MASK;
            if (srcSky > 1 && srcSky - 1 > (tgtVoxel & LIGHT_MASK))
                return true;
            const srcBlock = (srcVoxel >>> BLOCK_LIGHT_SHIFT) & BLOCK_LIGHT_MASK;
            if (srcBlock > 1 && srcBlock - 1 > ((tgtVoxel >>> BLOCK_LIGHT_SHIFT) & BLOCK_LIGHT_MASK))
                return true;
        }
    }
    return false;
}
/** Classification of a chunk's bottom-row light — see bottomRowSkyClass. */
export const BOTTOM_ROW_ALL_DARK = 0;
export const BOTTOM_ROW_ALL_FULL = 1;
export const BOTTOM_ROW_MIXED = 2;
/**
 * Classify the light a chunk hands DOWNWARD: all-dark, all-LIGHT_MAX, or mixed.
 *
 * A chunk's lighting depends on the chunk above it through exactly one quantity — the above chunk's
 * bottom row (ly=0) light, which feeds it both as `lightFromAbove` (getSunlitAbove) and as +Y border
 * injection. So when a chunk ARRIVES above an already-lit chunk, the one below needs re-lighting only
 * if that row differs from what it had already assumed:
 *   - it assumed OPEN SKY (no chunk above was loaded ⇒ every column at LIGHT_MAX) → unchanged iff
 *     the arrival's bottom row is ALL_FULL (a fully sky-lit air chunk — the common case above ground);
 *   - it assumed DARK (fully underground, below the column's lowest surface) → unchanged iff the
 *     arrival's bottom row is ALL_DARK (solid rock — the common case in the underground bulk).
 * Either way the relight is provably a no-op and can be skipped. This is the vertical counterpart of
 * faceDonatesLight, which already gates the +Y and horizontal faces; the below face was exempted
 * because a solid arrival caps an open-sky column, and that cap is exactly what this test detects.
 *
 * O(CHUNK_SIZE²) reads with an early exit, versus a full sky+block relight of the chunk below.
 */
export function bottomRowSkyClass(data) {
    let sawFull = false;
    let sawDark = false;
    for (let lz = 0; lz < CHUNK_SIZE; lz++) {
        const zOff = lz * Z_STRIDE;
        for (let lx = 0; lx < CHUNK_SIZE; lx++) {
            const light = data[lx + zOff] & LIGHT_MASK; // ly = 0 ⇒ no Y offset
            if (light === LIGHT_MAX)
                sawFull = true;
            else if (light === 0)
                sawDark = true;
            else
                return BOTTOM_ROW_MIXED;
            if (sawFull && sawDark)
                return BOTTOM_ROW_MIXED;
        }
    }
    return sawFull ? BOTTOM_ROW_ALL_FULL : BOTTOM_ROW_ALL_DARK;
}
/**
 * Build the lightFromAbove array for a chunk by checking the chunk directly above.
 *
 * If there is no chunk above loaded, assume full sunlight (return null).
 * When the chunk above eventually loads, it will relight this chunk with
 * the correct propagated values via ingestChunkData's neighbor relighting.
 *
 * If there IS a chunk above, scan its bottom row (ly=0) per column:
 *   light level = that voxel's light value (if non-solid), else 0.
 *
 * @param chunkAboveData  Voxel data of the chunk at (cx, cy+1, cz), or undefined
 * @returns               Uint8Array(CHUNK_SIZE²) with light levels, or null (null = all LIGHT_MAX)
 */
export function getSunlitAbove(chunkAboveData) {
    // No chunk above loaded → assume full sky exposure.
    // When the chunk above loads later, it will relight this chunk correctly.
    if (!chunkAboveData) {
        return null;
    }
    // Chunk above exists — check its bottom row (ly=0) per column
    const result = new Uint8Array(CHUNK_SIZE * CHUNK_SIZE);
    for (let lz = 0; lz < CHUNK_SIZE; lz++) {
        const zOff = lz * Z_STRIDE;
        for (let lx = 0; lx < CHUNK_SIZE; lx++) {
            const idx = lx + zOff; // ly=0, so no Y offset
            const voxel = chunkAboveData[idx];
            // Pass through the light level from the bottom of the chunk above
            result[lx + lz * CHUNK_SIZE] = voxel & LIGHT_MASK;
        }
    }
    return result;
}
// ============== Column Frontier Seeding (B2 optimization) ==============
/**
 * Module-level per-column "lowest lit Y" buffer, written by computeSunlightColumns.
 * Value is the lowest y with light > 0 from the column pass, or CHUNK_SIZE if fully dark.
 * Used by seedColumnFrontiers to find shadow-edge frontier voxels in O(CHUNK_SIZE²)
 * instead of scanning all CHUNK_SIZE³ voxels.
 */
const litBottom = new Int32Array(CHUNK_SIZE * CHUNK_SIZE);
/** Horizontal neighbor deltas for column frontier detection: [colIndex delta, voxel delta] */
const H_COL_DELTA = [
    [1, 1], // +X
    [-1, -1], // -X
    [CHUNK_SIZE, Z_STRIDE], // +Z
    [-CHUNK_SIZE, -Z_STRIDE], // -Z
];
/**
 * Collect BFS seeds from sunlight column shadow boundaries.
 *
 * After computeSunlightColumns fills litBottom[], this function does an O(CHUNK_SIZE²)
 * 2D scan comparing adjacent columns' lit ranges. Seeds are voxels with light that
 * have a darker horizontal neighbor — the BFS frontier.
 *
 * Two cases:
 * 1. Height difference: one column is lit deeper than its neighbor → seed the lit
 *    voxels in the range where the neighbor is dark.
 * 2. Light level difference: both columns lit at the same range but with different
 *    light values (from varying lightFromAbove) → seed one voxel at the boundary.
 */
function seedColumnFrontiers(data, startTail) {
    let tail = startTail;
    for (let z = 0; z < CHUNK_SIZE; z++) {
        const zOff = z * Z_STRIDE;
        for (let x = 0; x < CHUNK_SIZE; x++) {
            const colIdx = x + z * CHUNK_SIZE;
            const myBot = litBottom[colIdx];
            if (myBot >= CHUNK_SIZE)
                continue; // fully dark column, skip
            const colBase = x + zOff;
            for (let d = 0; d < 4; d++) {
                // Bounds check for each horizontal direction
                if (d === 0 && x >= MAX)
                    continue; // +X
                if (d === 1 && x <= 0)
                    continue; // -X
                if (d === 2 && z >= MAX)
                    continue; // +Z
                if (d === 3 && z <= 0)
                    continue; // -Z
                const nbrBot = litBottom[colIdx + H_COL_DELTA[d][0]];
                if (nbrBot > myBot) {
                    // Case 1: Neighbor dark where we're lit — seed our frontier voxels.
                    // BFS from each seed spreads light horizontally into the dark neighbor.
                    const yMax = nbrBot - 1 < MAX ? nbrBot - 1 : MAX;
                    for (let y = myBot; y <= yMax; y++) {
                        bfsQueue[tail++] = colBase + y * Y_STRIDE;
                    }
                }
                else if (nbrBot < CHUNK_SIZE) {
                    // Case 2: Both lit — check light level difference at overlap boundary.
                    // Different lightFromAbove values can create a gradient between adjacent
                    // columns even when both have the same lit range.
                    const checkY = myBot > nbrBot ? myBot : nbrBot;
                    if (checkY <= MAX) {
                        const yOff = checkY * Y_STRIDE;
                        const myLight = data[colBase + yOff] & LIGHT_MASK;
                        const nbrLight = data[colBase + H_COL_DELTA[d][1] + yOff] & LIGHT_MASK;
                        if (myLight > nbrLight + 1) {
                            // Seed one voxel — BFS propagates vertically through the neighbor column
                            bfsQueue[tail++] = colBase + yOff;
                        }
                    }
                }
            }
        }
    }
    return tail;
}
// ============== BFS Light Propagation ==============
/** Flat index neighbor deltas: +X, -X, +Y, -Y, +Z, -Z */
const NEIGHBOR_DELTAS = new Int32Array([1, -1, Y_STRIDE, -Y_STRIDE, Z_STRIDE, -Z_STRIDE]);
/** Per-voxel valid neighbor mask (6 bits). Pre-computed once at module load. */
const VALID_NEIGHBORS = new Uint8Array(VOXELS_PER_CHUNK);
(function initValidNeighbors() {
    const MAX = CHUNK_SIZE - 1;
    for (let z = 0; z < CHUNK_SIZE; z++) {
        for (let y = 0; y < CHUNK_SIZE; y++) {
            for (let x = 0; x < CHUNK_SIZE; x++) {
                let mask = 0x3F; // all 6 valid
                if (x === MAX)
                    mask &= ~1; // no +X
                if (x === 0)
                    mask &= ~2; // no -X
                if (y === MAX)
                    mask &= ~4; // no +Y
                if (y === 0)
                    mask &= ~8; // no -Y
                if (z === MAX)
                    mask &= ~16; // no +Z
                if (z === 0)
                    mask &= ~32; // no -Z
                VALID_NEIGHBORS[x + y * Y_STRIDE + z * Z_STRIDE] = mask;
            }
        }
    }
})();
/** Pre-allocated BFS queue — reused across calls (single-threaded main thread).
 *  Sized 2× chunk volume: frontier seeding keeps seeds small, but BFS propagation
 *  can enqueue voxels multiple times (from different sources with increasing light). */
const bfsQueue = new Uint32Array(VOXELS_PER_CHUNK * 4);
/**
 * Propagate light via BFS within a single chunk (in-place).
 *
 * Seeds only *frontier* voxels — those with light > 1 that have at least one
 * valid neighbor with light < (srcLight - 1). This avoids seeding the vast
 * uniformly-lit sky interior and prevents queue overflow.
 *
 * Spreads to 6-face neighbors: each step decrements by 1.
 * Only updates a voxel if the new light > its current light.
 * Opaque solid voxels block propagation.
 *
 * Sky light only — emitters are handled by the block-light channel
 * (see computeBlockLight), not stamped into the sky field.
 *
 * @param data The chunk's Uint32Array (CHUNK_SIZE³ entries), modified in-place
 */
export function propagateLight(data, preSeededTail) {
    const usePreSeeds = preSeededTail !== undefined;
    let head = 0;
    let tail = usePreSeeds ? preSeededTail : 0;
    // Seed frontier voxels — only when NOT using pre-collected seeds.
    // When pre-seeded, column frontiers + border inject seeds are already in the queue.
    if (!usePreSeeds) {
        for (let i = 0; i < VOXELS_PER_CHUNK; i++) {
            const light = data[i] & LIGHT_MASK;
            if (light <= 1)
                continue;
            const threshold = light - 1;
            const validDirs = VALID_NEIGHBORS[i];
            let frontier = false;
            for (let d = 0; d < 6; d++) {
                if (!(validDirs & (1 << d)))
                    continue;
                if ((data[i + NEIGHBOR_DELTAS[d]] & LIGHT_MASK) < threshold) {
                    frontier = true;
                    break;
                }
            }
            if (frontier) {
                bfsQueue[tail++] = i;
            }
        }
    }
    // BFS — process queue in-place
    while (head < tail) {
        const idx = bfsQueue[head++];
        const srcLight = data[idx] & LIGHT_MASK;
        if (srcLight <= 1)
            continue;
        const newLight = srcLight - 1;
        const validDirs = VALID_NEIGHBORS[idx];
        for (let d = 0; d < 6; d++) {
            if (!(validDirs & (1 << d)))
                continue;
            const nIdx = idx + NEIGHBOR_DELTAS[d];
            const nVoxel = data[nIdx];
            // Skip opaque solid voxels (single LUT lookup; mask off block/spare bits)
            if (VOXEL_BLOCKS_LIGHT[(nVoxel & VOXEL_STATIC_MASK) >> LIGHT_BITS])
                continue;
            // Only update if new light > existing
            if (newLight > (nVoxel & LIGHT_MASK)) {
                data[nIdx] = (nVoxel & LIGHT_CLEAR) | newLight;
                bfsQueue[tail++] = nIdx;
            }
        }
    }
}
/**
 * Scratch for computeSkyLight's return value: one object reused per call, in the same spirit as
 * `litBottom` / `bfsQueue` above (this file is on the ingest hot path and must not allocate per
 * chunk). Valid only until the next computeSkyLight call — computeAndPropagateLight consumes it
 * immediately, which is the only reason a shared instance is safe.
 */
const skyScanContent = { anyEmitter: false, anyBlockField: false };
/**
 * Combined lighting pipeline: column pass → frontier seed → border inject → BFS.
 *
 * This is the optimized entry point that fuses seed collection into the column and
 * border-inject passes, eliminating the O(CHUNK_SIZE³) frontier scan in propagateLight.
 * Column frontiers are found via an O(CHUNK_SIZE²) 2D shadow-height comparison.
 *
 * @param data           Chunk voxel data (modified in-place)
 * @param lightFromAbove Per-column light from chunk above (null = full sky)
 * @param neighbors      6 face-adjacent neighbor data arrays (null if not loaded)
 */
export function computeAndPropagateLight(data, lightFromAbove, neighbors, blockFalloff = 1) {
    const content = computeSkyLight(data, lightFromAbove, neighbors);
    // Independent emitter-driven light in the high bits. Reuses the same bfsQueue
    // sequentially — the sky BFS has fully drained it. Returns whether any block
    // light ended up present (used to gate incremental relights). The content flags come from the
    // column pass above, which saw every voxel anyway — so the common no-emitter chunk skips the block
    // channel without a scan of its own.
    // `blockFalloff` is the coarse levels' per-voxel cost — see propagateBlockLight. Sky light is NOT
    // scaled: its −1 per voxel is about how far light reaches DOWN past an obstruction, and a coarse
    // chunk's obstructions are coarse too, so the fine step would over-shadow every ring.
    return computeBlockLight(data, neighbors, content, blockFalloff);
}
/**
 * Compute only the SKY-light channel (in-place): column pass → frontier seed →
 * border inject → BFS. Sky light is anchored by the sun column, so it is
 * order-independent across chunks and safe to recompute per chunk in any order.
 */
export function computeSkyLight(data, lightFromAbove, neighbors) {
    computeSunlightColumns(data, lightFromAbove, skyScanContent);
    let tail = seedColumnFrontiers(data, 0);
    tail = injectBorderLight(data, neighbors, tail);
    propagateLight(data, tail);
    return skyScanContent;
}
/**
 * Inject block light from neighbor chunk boundaries into this chunk's edge voxels.
 * Mirrors injectBorderLight but reads/writes the BLOCK_LIGHT field across all 6 faces
 * (block light has no directional "from above" column — borders carry it between chunks).
 *
 * @param data      This chunk's Uint32Array, modified in-place
 * @param neighbors 6 neighbor data arrays (+X, -X, +Y, -Y, +Z, -Z), null if not loaded
 * @param seedTail  Current BFS queue tail
 * @returns         Updated BFS queue tail
 */
function injectBorderBlockLight(data, neighbors, seedTail) {
    let tail = seedTail;
    for (let face = 0; face < 6; face++) {
        const nData = neighbors[face];
        if (!nData)
            continue;
        const [ourBase, nbrBase, s1, s2] = FACE_DESC[face];
        for (let a = 0; a < CHUNK_SIZE; a++) {
            const aOff = a * s1;
            for (let b = 0; b < CHUNK_SIZE; b++) {
                const nbrIdx = nbrBase + aOff + b * s2;
                const nbrLight = (nData[nbrIdx] >>> BLOCK_LIGHT_SHIFT) & BLOCK_LIGHT_MASK;
                if (nbrLight <= 1)
                    continue;
                const ourIdx = ourBase + aOff + b * s2;
                const ourVoxel = data[ourIdx];
                // Skip opaque solid voxels (block light is blocked the same as sky light)
                if (VOXEL_BLOCKS_LIGHT[(ourVoxel & VOXEL_STATIC_MASK) >> LIGHT_BITS])
                    continue;
                const newLight = nbrLight - 1;
                if (newLight > ((ourVoxel >>> BLOCK_LIGHT_SHIFT) & BLOCK_LIGHT_MASK)) {
                    data[ourIdx] = (ourVoxel & BLOCK_LIGHT_CLEAR) | (newLight << BLOCK_LIGHT_SHIFT);
                    if (newLight > 1)
                        bfsQueue[tail++] = ourIdx;
                }
            }
        }
    }
    return tail;
}
/** Zero the block-light field across a whole chunk (leaves sky/material/weight intact). */
export function clearBlockLight(data) {
    for (let i = 0; i < VOXELS_PER_CHUNK; i++) {
        if ((data[i] & BLOCK_LIGHT_FIELD) !== 0)
            data[i] &= BLOCK_LIGHT_CLEAR;
    }
}
/** True if the chunk contains any emitting voxel (block-light source). */
export function chunkHasEmitter(data) {
    for (let i = 0; i < VOXELS_PER_CHUNK; i++) {
        if (VOXEL_EMISSION[(data[i] & VOXEL_STATIC_MASK) >> LIGHT_BITS] > 0)
            return true;
    }
    return false;
}
/** True if the chunk currently holds any block light (any voxel with a non-zero block field). */
export function chunkHasBlockLight(data) {
    for (let i = 0; i < VOXELS_PER_CHUNK; i++) {
        if ((data[i] & BLOCK_LIGHT_FIELD) !== 0)
            return true;
    }
    return false;
}
/**
 * Propagate the block-light channel (in-place) WITHOUT clearing first — monotonic
 * (max-merge, only ever raises values). Stamps emitters, injects from loaded neighbor
 * borders, and floods −1/step (blocked by opaque solids, same as sky light).
 *
 * Because it never lowers a value, it is safe to run repeatedly to a fixed point across
 * a multi-chunk region (see VoxelWorld.relightBlockRegion): clear the whole region once,
 * then propagate until a full pass raises nothing. Removing a source therefore darkens
 * correctly (the clear does the lowering; propagate only rebuilds from live sources).
 *
 * `falloff` is the cost PER VOXEL, and it exists so a coarse chunk's lights reach the same distance
 * in METRES as the fine ones. A lod-n voxel is 2^n fine voxels wide, so a falloff of 2^n costs the
 * same per metre as 1 does at lod 0 — `light(d) = LIGHT_MAX − 4d` at every level, the same field
 * sampled on a coarser lattice. The alternative, dividing the EMISSION instead, holds the radius too
 * but dims the source: the shader's block term is `light²`, so a lod-2 lamp would render at 5% of its
 * brightness. Defaults to 1, so every LOD-0 and relight path is untouched.
 *
 * @returns true if this pass raised any voxel's block light.
 */
/**
 * The per-voxel block-light cost for a lod-`lod` chunk — `2^lod`, CAPPED so a lamp keeps a one-voxel
 * glow instead of none at all.
 *
 * The stride scaling exists to hold a lamp's radius constant in METRES (see propagateBlockLight), but
 * block light is a 5-bit field: `BLOCK_LIGHT_MAX` is 31, and the BFS refuses a step whose cost is not
 * strictly under the source's level. At lod 5 the honest cost is 32, so a full-strength emitter
 * cannot light even its own neighbour — every coarse level from 5 up went structurally black. The cap
 * holds the radius at exactly one voxel, which is what lod 4 already gave: one step needs f < 31, a
 * SECOND needs `31 - f > f`, so any f in [16, 30] lights the neighbours and stops. Above lod 4 the
 * glow is then wider in metres than it should be — the trade is "slightly too far" against "absent",
 * and absent is what shipped.
 */
export function coarseBlockFalloff(lod) {
    return Math.min(1 << lod, (BLOCK_LIGHT_MASK >> 1) + 1);
}
export function propagateBlockLight(data, neighbors, falloff = 1) {
    let head = 0;
    let tail = 0;
    let changed = false;
    // Stamp emitters (max-merge against whatever is already there).
    for (let i = 0; i < VOXELS_PER_CHUNK; i++) {
        const voxel = data[i];
        const emission = VOXEL_EMISSION[(voxel & VOXEL_STATIC_MASK) >> LIGHT_BITS];
        if (emission > 0 && emission > ((voxel >>> BLOCK_LIGHT_SHIFT) & BLOCK_LIGHT_MASK)) {
            data[i] = (voxel & BLOCK_LIGHT_CLEAR) | (emission << BLOCK_LIGHT_SHIFT);
            bfsQueue[tail++] = i;
            changed = true;
        }
    }
    // Inject block light from loaded neighbor borders.
    const injectedTail = injectBorderBlockLight(data, neighbors, tail);
    if (injectedTail !== tail)
        changed = true;
    tail = injectedTail;
    // BFS — spread block light, −`falloff` per step, max-merge, blocked by opaque solids.
    while (head < tail) {
        const idx = bfsQueue[head++];
        const srcLight = (data[idx] >>> BLOCK_LIGHT_SHIFT) & BLOCK_LIGHT_MASK;
        if (srcLight <= falloff)
            continue;
        const newLight = srcLight - falloff;
        const validDirs = VALID_NEIGHBORS[idx];
        for (let d = 0; d < 6; d++) {
            if (!(validDirs & (1 << d)))
                continue;
            const nIdx = idx + NEIGHBOR_DELTAS[d];
            const nVoxel = data[nIdx];
            if (VOXEL_BLOCKS_LIGHT[(nVoxel & VOXEL_STATIC_MASK) >> LIGHT_BITS])
                continue;
            if (newLight > ((nVoxel >>> BLOCK_LIGHT_SHIFT) & BLOCK_LIGHT_MASK)) {
                data[nIdx] = (nVoxel & BLOCK_LIGHT_CLEAR) | (newLight << BLOCK_LIGHT_SHIFT);
                bfsQueue[tail++] = nIdx;
                changed = true;
            }
        }
    }
    return changed;
}
/**
 * True if this neighbour's shared FACE carries block light that could enter the target — the block
 * analogue of faceDonatesLight, and the same `> 1` threshold injectBorderBlockLight itself uses
 * (a voxel with light ≤ 1 donates `newLight = 0`, which can never raise anything).
 *
 * Face-only on purpose: the whole-chunk `chunkHasBlockLight` would cost 32³ reads per neighbour —
 * six times more than the pass it is trying to skip.
 *
 * @param data Neighbour chunk voxels.
 * @param face The neighbour's face index in the +X,−X,+Y,−Y,+Z,−Z order the arrays use — i.e. the
 *   same index under which the neighbour is stored, since FACE_DESC pairs our border with theirs.
 */
export function faceDonatesBlockLight(data, face) {
    const [, nbrBase, s1, s2] = FACE_DESC[face];
    for (let a = 0; a < CHUNK_SIZE; a++) {
        const aOff = a * s1;
        for (let b = 0; b < CHUNK_SIZE; b++) {
            const light = (data[nbrBase + aOff + b * s2] >>> BLOCK_LIGHT_SHIFT) & BLOCK_LIGHT_MASK;
            if (light > 1)
                return true;
        }
    }
    return false;
}
/**
 * Compute the block-light channel for a single chunk in isolation (clear + propagate).
 * Correct on its own; for a multi-chunk incremental update after an edit, use the
 * region-based clear-once/propagate-to-fixed-point flow instead (VoxelWorld).
 *
 * PROVABLE NO-OP SKIP. The unconditional form cost MORE than the sky pass on a chunk with no
 * emitters at all — measured per chunk: `sky=1.117ms` vs `block=1.383ms`, i.e. 55% of every relight
 * spent rebuilding a channel that stays empty. It scanned all 32768 voxels to clear, then all 32768
 * again to stamp emitters that weren't there, then read six 32² borders.
 *
 * Three conditions make the whole pass a provable no-op, so the result is byte-identical:
 *   - no emitter in this chunk        → nothing to stamp
 *   - no existing block light here    → nothing to clear (the clear only writes non-zero fields)
 *   - no neighbour face donates       → nothing to inject, so the BFS has no seed
 * The first two come from ONE fused scan (the clear and the stamp each used to walk the array
 * separately); the third is a face test per loaded neighbour.
 *
 * @param content Facts already observed by the sky column pass (see ChunkContentFlags). When given,
 *   the detection scan above is skipped entirely — that scan is the whole remaining cost of the skip.
 * @returns true if the chunk ended up with any block light.
 */
export function computeBlockLight(data, neighbors, content, falloff = 1) {
    let anyEmitter;
    let anyBlockField;
    if (content !== undefined) {
        anyEmitter = content.anyEmitter;
        anyBlockField = content.anyBlockField;
    }
    else {
        anyEmitter = false;
        anyBlockField = false;
        for (let i = 0; i < VOXELS_PER_CHUNK; i++) {
            const voxel = data[i];
            if ((voxel & BLOCK_LIGHT_FIELD) !== 0)
                anyBlockField = true;
            if (VOXEL_EMISSION[(voxel & VOXEL_STATIC_MASK) >> LIGHT_BITS] > 0)
                anyEmitter = true;
            if (anyEmitter && anyBlockField)
                break;
        }
    }
    if (!anyEmitter && !anyBlockField) {
        let anyDonor = false;
        for (let face = 0; face < 6 && !anyDonor; face++) {
            const nData = neighbors[face];
            if (nData && faceDonatesBlockLight(nData, face))
                anyDonor = true;
        }
        if (!anyDonor)
            return false;
    }
    if (anyBlockField)
        clearBlockLight(data);
    return propagateBlockLight(data, neighbors, falloff);
}
// ============== Region relight (shared by commit, preview, and the lighting worker) ==============
/** Chunk-space face offsets in the +X,-X,+Y,-Y,+Z,-Z order the neighbour arrays expect. */
const REGION_FACE_OFFSETS = [
    [1, 0, 0], [-1, 0, 0], [0, 1, 0], [0, -1, 0], [0, 0, 1], [0, 0, -1],
];
/** Read the 6 face-neighbour voxel arrays for (cx,cy,cz) via an accessor, null where absent. */
function regionFaceNeighbors(getData, cx, cy, cz) {
    const out = new Array(6);
    for (let f = 0; f < 6; f++) {
        const [dx, dy, dz] = REGION_FACE_OFFSETS[f];
        out[f] = getData(cx + dx, cy + dy, cz + dz);
    }
    return out;
}
/**
 * Relight a region of chunks in place — the SINGLE lighting orchestration shared by edit commit,
 * build preview, and the off-thread lighting worker (so all three stay bit-identical, no drift).
 *
 * Data is reached only through `getData`, so the caller decides the backing store: live chunk data
 * (commit), temp preview buffers (preview), or a transferred snapshot Map (worker). Target arrays
 * are mutated; everything else `getData` returns is treated as read-only context (borders).
 *
 * Two passes, matching the previous per-chunk flow:
 *  1. SKY — for each `sky` target (caller MUST pass them ordered top-down per column so a chunk's
 *     freshly-relit neighbour above is visible), run the column + border + BFS pass. The light
 *     entering the top comes from the chunk above when `getData` has it, else `skyAbove`.
 *  2. BLOCK — clear every `block` target's block field once, then propagate to a fixed point across
 *     the block targets (monotonic max-merge, so removing an emitter darkens correctly). Skipped
 *     entirely when no target sets `block` (the caller gates on emitters / pre-existing block light).
 */
export function relightRegion(getData, targets) {
    // --- SKY ---
    for (const t of targets) {
        if (!t.sky)
            continue;
        const data = getData(t.cx, t.cy, t.cz);
        if (!data)
            continue;
        const above = getData(t.cx, t.cy + 1, t.cz);
        const lightFromAbove = above ? getSunlitAbove(above) : (t.skyAbove ?? null);
        computeSkyLight(data, lightFromAbove, regionFaceNeighbors(getData, t.cx, t.cy, t.cz));
    }
    // --- BLOCK (clear-once, propagate to fixed point) ---
    const blockTargets = targets.filter((t) => t.block);
    if (blockTargets.length === 0)
        return;
    for (const t of blockTargets) {
        const data = getData(t.cx, t.cy, t.cz);
        if (data)
            clearBlockLight(data);
    }
    for (let pass = 0; pass < 4; pass++) {
        let changed = false;
        for (const t of blockTargets) {
            const data = getData(t.cx, t.cy, t.cz);
            if (!data)
                continue;
            if (propagateBlockLight(data, regionFaceNeighbors(getData, t.cx, t.cy, t.cz)))
                changed = true;
        }
        if (!changed)
            break;
    }
}
//# sourceMappingURL=lighting.js.map